// Vue « Loges » › un projet : étapes, dossiers, dépôt par glisser-déposer, fichiers (version, légende, téléchargement,
// corbeille), « Demander une décision », fil des notes et comptes rendus. Contrat : docs/LOGE-API.md.
// Un dossier déposé (ou choisi) est parcouru : un export KeyShotXR y devient un rendu 360°, le reste part fichier par fichier.
import { baseName, canRequestDecision, cleanName, isWaiting, nextRound, pad2, stepLabel, type LogeLang } from '../core/loge';
import { xrSummary } from '../core/logeXr';
import { logeStudio, type LogeFile, type LogeFolder, type LogeProposal, type StudioOverview, type StudioProject } from '../data/loge';
import { el, esc, on } from './dom';
import { icon } from './icons';
import type { LogeRoute } from './loge';
import { openCoverCrop } from './logeCoverCrop';
import { openDeleteDialog, openNewFolder } from './logeDialogs';
import { fmtDay, fmtLong, fmtSize, fmtWhen, licon, mailToast, plural, proposalState } from './logeKit';
import { clearDone, dismissUpload, enqueueDropped, enqueueUploads, onUploads, retryUpload, uploadJobs, type UploadJob } from './logeUploads';
import { showToast } from './toast';

/** Fichiers d'un dossier déposé, au plus : au-delà, ce n'est pas un dépôt voulu. */
const MAX_DROPPED_FILES = 5000;

/** Éléments déposés, lus tout de suite (le navigateur les oublie après l'événement) ; null s'il n'y a aucun dossier. */
function droppedEntries(dt: DataTransfer | null | undefined): FileSystemEntry[] | null {
  if (!dt?.items?.length) return null;
  const out: FileSystemEntry[] = [];
  for (const it of [...dt.items]) {
    if (it.kind !== 'file') continue;
    const e = typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null;
    if (e) out.push(e);
  }
  return out.some((e) => e.isDirectory) ? out : null;
}

/** Tous les fichiers des éléments déposés, avec leur chemin relatif (« Export/Montre_XR.72/0_0.jpg »). */
async function walkEntries(entries: FileSystemEntry[]): Promise<Array<{ path: string; file: File }>> {
  const out: Array<{ path: string; file: File }> = [];
  const visit = async (e: FileSystemEntry, prefix: string): Promise<void> => {
    if (out.length > MAX_DROPPED_FILES) throw new Error(`Dossier trop fourni : ${MAX_DROPPED_FILES} fichiers au plus.`);
    if (e.isFile) {
      const file = await new Promise<File>((resolve, reject) => (e as FileSystemFileEntry).file(resolve, reject));
      out.push({ path: `${prefix}${e.name}`, file });
    } else if (e.isDirectory) {
      const reader = (e as FileSystemDirectoryEntry).createReader();
      // Le navigateur rend le contenu d'un dossier par lots (100 dans Chrome) : on lit jusqu'au lot vide.
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
        if (!batch.length) break;
        for (const c of batch) await visit(c, `${prefix}${e.name}/`);
      }
    }
  };
  for (const e of entries) await visit(e, '');
  return out;
}

export interface ProjectView {
  projectId: string;
  destroy(): void;
  focus(folderId?: string, fileId?: string): void;
  setOverview(o: StudioOverview | null): void;
  refresh(): void;
}

interface Ctx {
  projectId: string;
  folderId: string | null;
  fileId: string | null;
  overview: StudioOverview | null;
  go(r: LogeRoute): void;
  refreshOverview(): void;
}

interface AskState { open: boolean; folderId: string | null; selected: Set<string>; title: string; caption: string; advances: boolean; notify: boolean; touched: boolean }

export function mountProjectView(view: HTMLElement, ctx: Ctx): ProjectView {
  let data: StudioProject | null = null;
  let error: string | null = null;
  let overview = ctx.overview;
  let folderId: string | null = ctx.folderId;
  let highlight: string | null = ctx.fileId;
  let pendingReload = false;
  let reloading: Promise<void> | null = null;
  let destroyed = false;
  const ask: AskState = { open: false, folderId: null, selected: new Set(), title: '', caption: '', advances: true, notify: true, touched: false };

  view.innerHTML = `<div class="loge-project" data-project>
    <div data-head><section class="page-head"><div><div class="eyebrow">Projet</div><h1>Chargement…</h1></div></section></div>
    <div class="panel"><div class="loading-rows"><div class="skeleton"></div><div class="skeleton"></div></div></div>
  </div>`;
  const root = el(view, '[data-project]');

  // ── Données ───────────────────────────────────────────────────────────────────────────────────

  function client(): { id: string; name: string; lang: LogeLang; downloadsUnlocked: boolean } | null {
    if (!data) return null;
    if (data.client?.name) return data.client;
    const c = overview?.clients.find((x) => x.id === data!.project.clientId || x.projects.some((p) => p.id === ctx.projectId));
    return c ? { id: c.id, name: c.name, lang: c.lang, downloadsUnlocked: c.downloadsUnlocked } : null;
  }

  const folderOf = (id: string | null): LogeFolder | undefined => data?.folders.find((f) => f.id === id);
  /** Fichiers visibles d'un dossier : ni à la corbeille, ni encore en cours d'envoi. */
  const filesIn = (fid: string): LogeFile[] => (data?.files ?? []).filter((f) => f.folderId === fid && !f.deletedAt && !f.incomplete);
  const trashIn = (fid: string): LogeFile[] => (data?.files ?? []).filter((f) => f.folderId === fid && !!f.deletedAt && !f.incomplete);

  function defaultFolder(): string | null {
    if (!data) return null;
    const cur = data.folders.find((f) => f.kind === 'studio' && f.step === data!.project.currentStep && !f.parentId);
    return (cur ?? data.folders.find((f) => f.kind === 'studio') ?? data.folders[0])?.id ?? null;
  }

  async function load(): Promise<void> {
    reloading ??= logeStudio.project(ctx.projectId)
      .then((d) => { data = d; error = null; })
      .catch((e: unknown) => { error = e instanceof Error ? e.message : String(e); })
      .finally(() => { reloading = null; });
    await reloading;
    if (destroyed) return;
    if (data) {
      if (highlight) { const f = data.files.find((x) => x.id === highlight); if (f) folderId = f.folderId; }
      if (!folderOf(folderId) || folderOf(folderId)?.kind === 'cover') folderId = defaultFolder();
    }
    paint();
    if (highlight) scrollToFile(highlight);
  }

  /** Relit le projet, sauf pendant une saisie : la relecture attend que le champ soit quitté. */
  function reload(): void {
    const a = document.activeElement as HTMLElement | null;
    if (a && root.contains(a) && a.matches('input[type="text"], input:not([type]), textarea')) { pendingReload = true; return; }
    pendingReload = false;
    void load();
  }

  // ── Rendu ─────────────────────────────────────────────────────────────────────────────────────

  function paint(): void {
    if (!data) {
      root.innerHTML = error
        ? `<nav class="loge-path"><button type="button" data-go-overview>Loges</button></nav><div class="banner bad" role="alert">${icon('alert')}<div><b>Impossible d’ouvrir ce projet.</b><br>${esc(error)}</div><button class="btn small" type="button" data-reload>${icon('refresh')}Réessayer</button></div>`
        : root.innerHTML;
      return;
    }
    const p = data.project;
    const c = client();
    const folder = folderOf(folderId);
    const step = p.steps[p.currentStep - 1];
    const open = data.proposals.filter((x) => x.status === 'open').length;
    const cover = p.coverFileId ? data.files.find((x) => x.id === p.coverFileId && !x.deletedAt)?.previews[0] : undefined;
    const coverUp = uploadJobs(ctx.projectId).some((j) => j.cover && j.status !== 'done' && j.status !== 'error');
    root.innerHTML = `
      <nav class="loge-path" aria-label="Fil d’Ariane"><button type="button" data-go-overview>Loges</button><span aria-hidden="true">›</span>${c ? `<button type="button" data-go-client="${esc(c.id)}">${esc(c.name)}</button><span aria-hidden="true">›</span>` : ''}<b>${esc(p.name)}</b>${folder ? `<span aria-hidden="true">›</span>${folderPath(folder)}` : ''}</nav>
      <section class="page-head loge-head-cover"><label class="loge-cover${cover ? '' : ' empty'}" title="Couverture du projet : la carte et l’en-tête du projet chez le client">
          ${cover ? `<img src="${esc(cover)}" alt="" data-thumb data-tag="">` : `${licon('image')}`}
          <span>${coverUp ? 'Envoi…' : cover ? 'Changer la couverture' : 'Ajouter une couverture'}</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" data-cover-input class="sr" ${coverUp || !c ? 'disabled' : ''}></label>
        <div><div class="eyebrow">Projet ${pad2(p.number)}${p.kind ? ` · ${esc(p.kind)}` : ''}</div><h1>${esc(p.name)}</h1>
        <p>Étape en cours : ${step ? `${pad2(p.currentStep)} · ${esc(stepLabel(step, 'fr'))}` : p.currentStep}${open ? ` · ${open > 1 ? `${open} décisions en attente` : '1 décision en attente'}` : ''}</p></div>
        <div class="page-actions"><button class="btn" type="button" data-reload title="Relire">${icon('refresh')}<span class="sr">Relire</span></button><button class="btn primary" type="button" data-ask-open>${licon('send')}Demander une décision</button></div></section>
      <ol class="loge-steps" aria-label="Étapes du projet">${p.steps.map((s) => `<li class="${s.state}"><button type="button" data-step-folder="${s.n}" ${s.state === 'now' ? 'aria-current="step"' : ''}><span class="n">${pad2(s.n)}</span><span class="l">${esc(stepLabel(s, 'fr'))}</span>${s.state === 'now' ? '<small>en cours</small>' : s.state === 'done' ? '<small>validée</small>' : ''}</button></li>`).join('')}</ol>
      <div class="loge-work">
        <aside class="loge-tree" aria-label="Dossiers"><div class="loge-tree-head"><span class="eyebrow">Dossiers</span><button class="icon-btn" type="button" data-new-folder title="Nouveau dossier" aria-label="Nouveau dossier">${licon('folder-plus')}</button></div>${tree()}</aside>
        <section class="loge-files" data-files-col>${folder ? folderBlock(folder) : '<p class="hint">Aucun dossier.</p>'}</section>
        <aside class="loge-side">${askPanel()}${proposalsCard()}${reportsCard()}${peopleCard()}${downloadsCard()}${activityCard()}${lifeCard()}</aside>
      </div>`;
    paintUploads();
  }

  function folderPath(f: LogeFolder): string {
    const chain: LogeFolder[] = [];
    let cur: LogeFolder | undefined = f;
    for (let i = 0; cur && i < 8; i++) { chain.unshift(cur); cur = folderOf(cur.parentId); }
    return chain.map((x, i) => i === chain.length - 1 ? `<b>${esc(x.name)}</b>` : `<button type="button" data-folder="${esc(x.id)}">${esc(x.name)}</button><span aria-hidden="true">›</span>`).join('');
  }

  function tree(): string {
    if (!data) return '';
    const kids = (pid: string | null) => data!.folders.filter((f) => f.parentId === pid && f.kind !== 'cover');
    const item = (f: LogeFolder, depth: number): string => {
      const n = filesIn(f.id).filter((x) => x.latest).length;
      const now = f.step === data!.project.currentStep && f.kind === 'studio' && !f.parentId;
      return `<li><button type="button" class="loge-folder${f.kind === 'client' ? ' client' : ''}" data-folder="${esc(f.id)}" style="--depth:${depth}" ${f.id === folderId ? 'aria-current="true"' : ''}>${licon(f.kind === 'client' ? 'upload' : 'folder')}<span>${esc(f.name)}</span>${now ? '<i class="loge-now" title="Étape en cours"></i>' : ''}${n ? `<small>${n}</small>` : ''}</button>${kids(f.id).length ? `<ul>${kids(f.id).map((k) => item(k, depth + 1)).join('')}</ul>` : ''}</li>`;
    };
    return `<ul class="loge-tree-list">${kids(null).map((f) => item(f, 0)).join('')}</ul>
      <button class="btn small ghost loge-tree-add" type="button" data-new-folder>${icon('plus')}Nouveau dossier</button>`;
  }

  function folderBlock(f: LogeFolder): string {
    const files = filesIn(f.id);
    const trash = trashIn(f.id);
    const p = data!.project;
    const c = client();
    const unlocked = p.downloadsUnlocked || !!c?.downloadsUnlocked;
    const groups = groupVersions(files);
    // Le dossier 00 porte l'étape 0 pour se ranger en tête : ce n'est pas une étape du projet.
    const stepTag = f.step ? `Étape ${pad2(f.step)}${f.step === p.currentStep ? ' · en cours' : f.step < p.currentStep ? ' · validée' : ' · à venir'}`
      : f.kind === 'client' ? 'Dossier du client' : 'Contrat et factures';
    const drop = f.kind === 'client'
      ? `<p class="loge-client-note">${licon('upload')}Les fichiers que le client envoie depuis sa Loge arrivent ici. Ils sont toujours téléchargeables.</p>`
      : c
        ? `<label class="loge-drop" data-drop><input type="file" multiple data-file-input class="sr" tabindex="-1">${licon('upload')}<h3>Glissez vos fichiers ici</h3><p>PDF, STEP, images, vidéos, ZIP, dossiers · rangés dans « ${esc(f.name)} » · ou <u>choisissez-les</u></p><small>Les images et les PDF partent avec un aperçu filigrané « ACTE·5 · ${esc(c.lang === 'en' ? 'Confidential' : 'Confidentiel')} » ; un STEP, un IGES ou un GLB, avec une maquette 3D de consultation et son affiche ; un export KeyShotXR (son dossier ou son ZIP), en rendu 360°. Le client ne voit qu’eux.</small></label>
          <p class="loge-drop-more"><button class="loge-link" type="button" data-pick-folder>${licon('folder')}Choisir un dossier (export KeyShotXR)</button><input type="file" data-folder-input class="sr" tabindex="-1" aria-hidden="true" webkitdirectory></p>`
        : `<div class="loge-drop disabled">${licon('upload')}<h3>Lecture de la maison…</h3><p>Le dépôt s’ouvre dès que la langue du client (pour le filigrane) est connue.</p></div>`;
    // Rouvrir une étape validée (le client revient sur sa validation) ou avancer sans validation : à la main.
    const makeCurrent = f.kind === 'studio' && !f.parentId && f.step !== null && f.step !== p.currentStep && p.status !== 'completed'
      ? `<button class="btn small ghost" type="button" data-make-current="${f.step}">${licon('flag')}${f.step < p.currentStep ? 'Rouvrir cette étape' : 'Passer à cette étape'}</button>`
      : '';
    return `
      <div class="loge-folder-head"><div><span class="eyebrow">${esc(stepTag)}</span><h2>${esc(f.name)}</h2></div>
        <span class="loge-folder-tools">${makeCurrent}<span class="hint">${plural(groups.length, 'document', 'documents')}</span></span></div>
      ${drop}
      <div data-uploads></div>
      ${groups.length ? `<p class="hint loge-files-hint">${unlocked ? `${licon('unlock')}Téléchargements débloqués pour ce projet : le client peut télécharger les originaux.` : `${licon('lock')}Consultation seule jusqu’au règlement du projet. « Téléchargeable » autorise un fichier dès maintenant.`}</p>
        <div class="loge-filelist">${groups.map((g) => fileRow(g[0]!, g.slice(1), unlocked)).join('')}</div>`
        : f.kind === 'client' ? '<p class="loge-empty-line">Rien d’envoyé pour l’instant.</p>' : ''}
      ${trash.length ? `<details class="loge-trash"><summary>${licon('trash')}Corbeille · ${plural(trash.length, 'fichier', 'fichiers')}, gardés 30 jours</summary>${trash.map((t) => `<div class="loge-trash-row"><span>${esc(t.name)} <small>V${t.version} · retiré le ${esc(fmtDay(t.deletedAt))}</small></span><button class="btn small ghost" type="button" data-restore="${esc(t.id)}">${icon('restore')}Restaurer</button></div>`).join('')}</details>` : ''}`;
  }

  /** Versions d'un même document regroupées (la plus récente d'abord), documents du plus récent au plus ancien. */
  function groupVersions(files: LogeFile[]): LogeFile[][] {
    const map = new Map<string, LogeFile[]>();
    for (const f of files) {
      const k = baseName(f.name);
      const list = map.get(k) ?? [];
      list.push(f);
      map.set(k, list);
    }
    const groups = [...map.values()].map((g) => g.sort((a, b) => b.version - a.version));
    return groups.sort((a, b) => (b[0]!.createdAt || '').localeCompare(a[0]!.createdAt || ''));
  }

  function thumb(f: LogeFile): string {
    return f.previews[0]
      ? `<span class="loge-th"><img src="${esc(f.previews[0])}" alt="" loading="lazy" data-thumb data-tag="${esc(f.tag)}"></span>`
      : `<span class="loge-th tag" aria-hidden="true">${esc(f.tag)}</span>`;
  }

  function fileRow(f: LogeFile, older: LogeFile[], unlocked: boolean): string {
    const inDecision = data!.proposals.some((x) => isWaiting(x.status) && x.fileIds.includes(f.id));
    const pages = f.previews.length;
    const fromClient = f.uploadedBy === 'client';
    const meta = [fmtSize(f.size), fromClient ? `envoyé par ${f.memberName ?? 'le client'} le ${fmtDay(f.createdAt)}` : `déposé le ${fmtDay(f.createdAt)}`,
      f.model ? 'maquette 3D' : f.xr ? `KeyShotXR · ${xrSummary(f.xr)}` : f.family === 'pdf' && pages ? `${pages} page${pages > 1 ? 's' : ''} en aperçu` : !pages && !fromClient ? 'sans aperçu' : ''].filter(Boolean).map(esc).join(' · ');
    const allow = fromClient
      ? `<button class="btn small" type="button" data-download="${esc(f.id)}" data-name="${esc(f.name)}">${icon('download')}Télécharger</button>`
      : `<label class="loge-switch" title="Autoriser le téléchargement dès maintenant"><input type="checkbox" data-allow="${esc(f.id)}" ${f.allowDownload || unlocked ? 'checked' : ''} ${unlocked ? 'disabled' : ''}><span class="track" aria-hidden="true"></span><span class="lbl">Téléchargeable</span></label>`;
    return `<div class="loge-file${f.id === highlight ? ' highlight' : ''}" data-file-row="${esc(f.id)}">
      ${thumb(f)}
      <div class="loge-file-main">
        <div class="loge-file-title"><b>${esc(f.name)}</b><span class="loge-v">V${f.version}</span>${inDecision ? '<span class="loge-tag wait">En décision</span>' : ''}</div>
        <small>${meta}</small>
        <input class="loge-cap" type="text" maxlength="200" data-caption="${esc(f.id)}" value="${esc(f.caption)}" placeholder="Ajouter une légende…" aria-label="Légende de ${esc(f.name)}">
        ${older.length ? `<small class="loge-older">Versions précédentes : ${older.map((o) => `V${o.version} · ${esc(fmtDay(o.createdAt))}`).join(' ; ')}</small>` : ''}
      </div>
      <div class="loge-file-act">${f.model ? `<button class="btn small ghost" type="button" data-view3d="${esc(f.id)}" title="La maquette telle que le client la voit">Voir en 3D</button>` : ''}${f.xr ? `<button class="btn small ghost" type="button" data-viewxr="${esc(f.id)}" title="Le rendu 360° tel que le client le voit">Tourner autour</button>` : ''}${f.stream ? `<button class="btn small ghost" type="button" data-play="${esc(f.id)}" title="La vidéo telle que le client la voit">Lire la vidéo</button>` : ''}${allow}<button class="icon-btn" type="button" data-trash="${esc(f.id)}" title="Mettre à la corbeille" aria-label="Mettre ${esc(f.name)} à la corbeille">${icon('trash')}</button></div>
    </div>`;
  }

  // ── Envois en cours ───────────────────────────────────────────────────────────────────────────

  function jobText(j: UploadJob): { bar: number; label: string } {
    const pct = j.total ? Math.min(100, Math.floor((j.sent / j.total) * 100)) : 0;
    switch (j.status) {
      case 'waiting': return { bar: 0, label: 'En attente' };
      case 'preview': return { bar: 0, label: j.previewNote || 'Préparation de l’aperçu…' };
      case 'uploading': return { bar: pct, label: pct >= 100 ? 'Finalisation…' : `Envoi… ${pct} %` };
      case 'done': return { bar: 100, label: 'Prêt' };
      case 'error': return { bar: pct, label: 'Interrompu' };
    }
  }

  function jobRow(j: UploadJob): string {
    const t = jobText(j);
    const other = j.folderId !== folderId ? ` · dans « ${esc(j.folderName)} »` : '';
    const version = j.version > 1 ? `Une V${j.version - 1} existe : celle-ci devient la V${j.version}` : fmtSize(j.file.size);
    return `<div class="loge-up" data-key="${esc(j.key)}" data-status="${j.status}">
      <span class="loge-th${j.thumbUrl ? '' : ' tag'}" aria-hidden="true">${j.thumbUrl ? `<img src="${esc(j.thumbUrl)}" alt="">` : esc((j.file.name.split('.').pop() ?? '').toUpperCase().slice(0, 5) || 'FICHIER')}</span>
      <div class="loge-up-name"><b>${esc(j.file.name)}</b><small>${j.version > 1 ? version : esc(fmtSize(j.file.size || j.total))}${other}${j.previewNote && j.status !== 'preview' ? ` · ${esc(j.previewNote)}` : ''}</small>${j.error ? `<small class="loge-up-error" role="alert">${esc(j.error)}</small>` : ''}</div>
      <div class="loge-up-prog"><div class="loge-bar"><i style="width:${t.bar}%"></i></div><small data-label>${esc(t.label)}</small></div>
      <div class="loge-up-act">${j.status === 'error' && !j.fatal ? `<button class="btn small" type="button" data-retry="${esc(j.key)}">${icon('refresh')}Réessayer</button>` : ''}${j.status !== 'done' ? `<button class="icon-btn" type="button" data-dismiss="${esc(j.key)}" title="${j.status === 'error' ? 'Retirer' : 'Annuler l’envoi'}" aria-label="${j.status === 'error' ? 'Retirer' : 'Annuler l’envoi'}">${icon('close')}</button>` : ''}</div>
    </div>`;
  }

  function paintUploads(): void {
    const box = root.querySelector<HTMLElement>('[data-uploads]');
    if (!box) return;
    const jobs = uploadJobs(ctx.projectId);
    const done = jobs.filter((j) => j.status === 'done').length;
    box.innerHTML = jobs.length
      ? `<div class="loge-ups"><div class="loge-ups-head"><span class="eyebrow">Envois</span>${done ? `<button class="btn small ghost" type="button" data-clear-done>Effacer les envois terminés</button>` : ''}</div>${jobs.map(jobRow).join('')}</div>`
      : '';
  }

  function updateJob(j: UploadJob): void {
    const row = root.querySelector<HTMLElement>(`.loge-up[data-key="${CSS.escape(j.key)}"]`);
    if (!row || row.dataset.status !== j.status) { paintUploads(); return; }
    const t = jobText(j);
    const bar = row.querySelector<HTMLElement>('.loge-bar i');
    if (bar) bar.style.width = `${t.bar}%`;
    const label = row.querySelector<HTMLElement>('[data-label]');
    if (label) label.textContent = t.label;
  }

  let reloadTimer: ReturnType<typeof setTimeout> | null = null;
  const offUploads = onUploads((j) => {
    if (destroyed) return;
    if (!j) { paintUploads(); return; }
    if (j.projectId !== ctx.projectId) return;
    updateJob(j);
    if (j.status === 'done') {
      if (j.fileId && ask.open && ask.folderId === j.folderId) ask.selected.add(j.fileId);
      if (reloadTimer) clearTimeout(reloadTimer);
      reloadTimer = setTimeout(reload, 400);
    }
  });

  // ── Colonne de droite ─────────────────────────────────────────────────────────────────────────

  function audience(): string[] {
    return (data?.team ?? []).filter((t) => !t.studio).map((t) => t.name);
  }

  function askPanel(): string {
    if (!data) return '';
    if (!ask.open) {
      return `<section class="card loge-card loge-ask-cta"><span class="eyebrow">Décision</span><p>Choisissez des fichiers du dossier ouvert : le client voit « Valider », « Laisser une note » ou « Réserver un appel ».</p><button class="btn primary wide" type="button" data-ask-open>${licon('send')}Demander une décision</button></section>`;
    }
    const f = folderOf(ask.folderId);
    if (!f || f.kind === 'client') {
      return `<section class="card loge-card loge-ask" data-ask-panel><div class="loge-card-head"><span class="eyebrow">Demander une décision</span><button class="icon-btn" type="button" data-ask-close aria-label="Fermer">${icon('close')}</button></div><p class="hint">Ouvrez d’abord le dossier d’une étape : la décision porte sur ses fichiers.</p></section>`;
    }
    const p = data.project;
    // Une décision ne se demande que sur l'étape en cours : le client ne voit jamais celle d'une étape à venir.
    if (!canRequestDecision(f.step, p.currentStep)) {
      const cur = p.steps[p.currentStep - 1];
      const curLabel = cur ? `${pad2(p.currentStep)} · ${esc(stepLabel(cur, 'fr'))}` : String(p.currentStep);
      const later = f.step !== null && f.step > p.currentStep;
      return `<section class="card loge-card loge-ask" data-ask-panel><div class="loge-card-head"><span class="eyebrow">Demander une décision</span><button class="icon-btn" type="button" data-ask-close aria-label="Fermer">${icon('close')}</button></div>
        <p class="hint">Les décisions se demandent sur l’étape en cours : <b>${curLabel}</b>.${later ? ' Pour cette étape, faites d’abord valider l’étape en cours par le client, ou passez-y vous-même.' : f.step !== null ? ' Pour revenir sur cette étape, rouvrez-la d’abord.' : ''}</p>
        <div class="loge-ask-alt"><button class="btn small" type="button" data-step-folder="${p.currentStep}">${licon('folder')}Ouvrir l’étape en cours</button>${f.step && f.kind === 'studio' && !f.parentId ? `<button class="btn small ghost" type="button" data-make-current="${f.step}">${licon('flag')}${later ? 'Passer à cette étape' : 'Rouvrir cette étape'}</button>` : ''}</div></section>`;
    }
    const files = groupVersions(filesIn(f.id)).map((g) => g[0]!);
    const step = f.step || p.currentStep;
    const round = nextRound(data.proposals.map((x) => ({ step: x.step, folder_id: x.folderId, round: x.round, status: x.status, created_at: x.createdAt })), step, f.id);
    const lastOpen = data.proposals.find((x) => x.folderId === f.id && isWaiting(x.status));
    const next = p.steps[step];
    const advanceHint = next ? `Validée, le projet passe à « ${pad2(step + 1)} · ${esc(stepLabel(next, 'fr'))} ».` : 'Dernière étape du projet.';
    const people = audience();
    return `<section class="card loge-card loge-ask" data-ask-panel aria-labelledby="lgAskTitle">
      <div class="loge-card-head"><span class="eyebrow" id="lgAskTitle">Demander une décision</span><button class="icon-btn" type="button" data-ask-close aria-label="Fermer">${icon('close')}</button></div>
      <p class="hint">Dossier « ${esc(f.name)} »${round > 1 ? ` · <b>round ${round}</b>${lastOpen ? ' : la demande encore ouverte de ce dossier sera remplacée' : ''}` : ''}.</p>
      <fieldset class="loge-pick"><legend>Fichiers soumis</legend>${files.length
        ? files.map((x) => `<label class="loge-pick-row"><input type="checkbox" data-ask-file="${esc(x.id)}" ${ask.selected.has(x.id) ? 'checked' : ''}>${thumb(x)}<span><b>${esc(x.name)}</b><small>V${x.version}${x.caption ? ` · ${esc(x.caption)}` : ''}</small></span></label>`).join('')
        : '<p class="hint">Ce dossier est vide : déposez d’abord les fichiers.</p>'}</fieldset>
      <div class="field"><label for="lgAskTitleIn">Titre</label><input id="lgAskTitleIn" type="text" maxlength="120" data-ask-title value="${esc(ask.title)}" placeholder="Rendus 3D"></div>
      <div class="field"><label for="lgAskCaption">Légende</label><textarea id="lgAskCaption" rows="3" maxlength="400" data-ask-caption placeholder="Ce que le client doit regarder, ce qui a changé depuis la version précédente…">${esc(ask.caption)}</textarea></div>
      <label class="loge-opt"><input type="checkbox" data-ask-advances ${ask.advances ? 'checked' : ''}><span>Cette décision clôt l’étape<small>${advanceHint}</small></span></label>
      <label class="loge-opt"><input type="checkbox" data-ask-notify ${ask.notify ? 'checked' : ''}><span>Prévenir par e-mail<small>${people.length ? esc(people.join(', ')) : 'Personne n’a encore accès à ce projet : invitez quelqu’un depuis la maison.'}</small></span></label>
      <p class="error" role="alert" data-ask-error></p>
      <button class="btn primary wide" type="button" data-ask-send ${files.length ? '' : 'disabled'}>${licon('send')}Envoyer la demande</button>
    </section>`;
  }

  function proposalCard(x: LogeProposal): string {
    const st = proposalState(x.status, x.decidedAt);
    const f = folderOf(x.folderId);
    // Répondre aussi sous une proposition validée : le client peut revenir sur sa validation par une note.
    const canReply = x.status === 'open' || x.status === 'discussing' || x.status === 'approved';
    const lateNote = x.status === 'approved' && x.decidedAt !== null && x.notes.some((n) => n.author === 'client' && n.createdAt > x.decidedAt!);
    return `<article class="loge-prop" data-tone="${st.tone}">
      <header><b>${esc(x.title)}</b>${x.round > 1 ? `<span class="loge-tag">Round ${x.round}</span>` : ''}</header>
      <small class="loge-prop-meta"><span class="loge-state ${st.tone}">${esc(st.text)}</span> · ${f ? `${esc(f.name)} · ` : ''}${plural(x.fileIds.length, 'fichier', 'fichiers')} · ${esc(fmtDay(x.createdAt))}</small>
      ${x.caption ? `<p class="loge-prop-cap">${esc(x.caption)}</p>` : ''}
      ${lateNote ? `<p class="loge-late">${licon('flag')}Le client revient sur cette validation. Rouvrez l’étape pour lui soumettre une nouvelle version.</p>` : ''}
      ${x.notes.length ? `<ul class="loge-thread">${x.notes.map((n) => `<li class="${n.author}"><small><b>${esc(n.author === 'studio' ? 'ACTE.5' : n.name || 'Le client')}</b> · ${esc(fmtWhen(n.createdAt))}</small><p>${esc(n.body)}</p></li>`).join('')}</ul>` : ''}
      ${canReply && x.notes.some((n) => n.author === 'client') ? `<form class="loge-reply" data-reply="${esc(x.id)}"><input type="text" maxlength="600" placeholder="Répondre en une ligne…" aria-label="Réponse sur ${esc(x.title)}"><button class="btn small primary" type="submit">Répondre</button></form>` : ''}
      ${x.reportId ? `<button class="loge-link" type="button" data-report="${esc(x.reportId)}">${licon('report')}Compte rendu de validation</button>` : ''}
      ${isWaiting(x.status) ? `<button class="loge-link muted" type="button" data-withdraw="${esc(x.id)}">${licon('withdraw')}Retirer la demande</button>` : ''}
    </article>`;
  }

  function proposalsCard(): string {
    if (!data) return '';
    const list = [...data.proposals].sort((a, b) => rank(a) - rank(b) || b.createdAt.localeCompare(a.createdAt));
    const shown = list.filter((x) => (x.status !== 'replaced' && x.status !== 'withdrawn') || x.notes.length > 0).slice(0, 8);
    return `<section class="card loge-card"><div class="loge-card-head"><h2>Décisions</h2>${list.length ? `<span class="hint">${list.length}</span>` : ''}</div>
      ${shown.length ? shown.map(proposalCard).join('') : '<p class="hint">Aucune décision demandée pour l’instant.</p>'}</section>`;
  }

  function reportsCard(): string {
    if (!data?.reports.length) return '';
    return `<section class="card loge-card"><div class="loge-card-head"><h2>Comptes rendus</h2></div>
      ${data.reports.map((r) => `<button class="loge-link block" type="button" data-report="${esc(r.id)}">${licon('report')}<span>${esc(r.title || 'Validation')}<small>${esc(fmtLong(r.createdAt))}</small></span></button>`).join('')}</section>`;
  }

  function peopleCard(): string {
    if (!data) return '';
    const people = data.team.filter((t) => !t.studio);
    const c = client();
    return `<section class="card loge-card"><div class="loge-card-head"><h2>Personnes</h2>${c ? `<button class="btn small ghost" type="button" data-go-client="${esc(c.id)}">Gérer</button>` : ''}</div>
      ${people.length ? people.map((t) => `<div class="loge-team"><span class="loge-av small" aria-hidden="true">${esc(t.initials)}</span><span><b>${esc(t.name)}</b>${t.role ? `<small>${esc(t.role)}</small>` : ''}</span></div>`).join('') : '<p class="hint">Personne n’a encore accès à ce projet.</p>'}</section>`;
  }

  function downloadsCard(): string {
    if (!data) return '';
    const c = client();
    const p = data.project;
    const unlocked = p.downloadsUnlocked || p.clientUnlocked || !!c?.downloadsUnlocked;
    return `<section class="card loge-card"><div class="loge-card-head"><h2>Téléchargements</h2></div>
      ${unlocked
        ? `<p class="loge-state-line ok">${licon('unlock')}Débloqués${p.clientUnlocked || c?.downloadsUnlocked ? ' pour toute la maison' : ' pour ce projet'} : le client télécharge les originaux.</p>${p.clientUnlocked || c?.downloadsUnlocked ? '<p class="hint">Pour refermer, passez par la maison.</p>' : `<button class="btn small ghost" type="button" data-unlock-project data-lock="true">${licon('lock')}Refermer ce projet</button>`}`
        : `<p class="loge-state-line">${licon('lock')}Consultation seule : le client voit les aperçus filigranés, sauf les fichiers marqués « Téléchargeable ».</p><button class="btn small" type="button" data-unlock-project>${licon('unlock')}Débloquer ce projet</button>`}</section>`;
  }

  function activityCard(): string {
    if (!data?.activity.length) return '';
    return `<section class="card loge-card"><div class="loge-card-head"><h2>Activité</h2></div><ul class="loge-activity">${data.activity.slice(0, 8).map((a) => `<li class="${a.actor === 'client' ? 'client' : ''}"><i aria-hidden="true"></i><span>${esc(a.text)}<small>${esc(fmtWhen(a.createdAt))}</small></span></li>`).join('')}</ul></section>`;
  }

  /** Fin de vie du projet : l'archiver (réversible) ou l'effacer pour de bon. */
  function lifeCard(): string {
    if (!data) return '';
    const p = data.project;
    const archive = p.status === 'archived';
    return `<section class="card loge-card loge-life"><div class="loge-card-head"><h2>Ce projet</h2></div>
      ${archive
        ? `<p class="hint">Projet archivé : il ne figure plus dans La Loge du client. Rien n’est perdu.</p>
           <button class="btn small" type="button" data-archive-project="0">${icon('restore')}Rouvrir le projet</button>`
        : `<p class="hint">L’archiver le retire de La Loge du client et de votre liste des projets en cours. Rien n’est perdu : vous pouvez le rouvrir.</p>
           <button class="btn small" type="button" data-archive-project="1">${icon('check')}Archiver le projet</button>`}
      <p class="hint loge-life-sep">Le supprimer efface tout, jusqu’aux fichiers dans le stockage. Sans retour.</p>
      <button class="btn small danger" type="button" data-delete-project>${icon('trash')}Supprimer définitivement</button>
    </section>`;
  }

  function rank(x: LogeProposal): number { return x.status === 'open' ? 0 : x.status === 'discussing' ? 1 : x.status === 'approved' ? 2 : x.status === 'replaced' ? 3 : 4; }

  // ── Actions ───────────────────────────────────────────────────────────────────────────────────

  function selectFolder(id: string): void {
    if (!folderOf(id)) return;
    folderId = id;
    if (ask.open && ask.folderId !== id) openAsk();
    else paint();
  }

  function openAsk(): void {
    const f = folderOf(folderId);
    ask.open = true;
    ask.folderId = folderId;
    ask.selected = new Set();
    if (f && f.kind === 'studio') {
      const latest = groupVersions(filesIn(f.id)).map((g) => g[0]!);
      const fresh = uploadJobs(ctx.projectId).filter((j) => j.folderId === f.id && j.status === 'done' && j.fileId).map((j) => j.fileId!);
      const pre = fresh.filter((id) => latest.some((x) => x.id === id));
      (pre.length ? pre : latest.map((x) => x.id)).forEach((id) => ask.selected.add(id));
      if (!ask.touched || !ask.title) ask.title = f.name.replace(/^\d{2}\s*·\s*/, '');
    }
    paint();
    root.querySelector<HTMLElement>('[data-ask-panel]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  async function sendAsk(btn: HTMLButtonElement): Promise<void> {
    const f = folderOf(ask.folderId);
    const errBox = root.querySelector<HTMLElement>('[data-ask-error]');
    const fail = (m: string) => { if (errBox) errBox.textContent = m; };
    if (!data || !f) return;
    const title = cleanName(ask.title, 120);
    if (!title) { fail('Donnez un titre à la demande.'); return; }
    const fileIds = [...ask.selected].filter((id) => filesIn(f.id).some((x) => x.id === id));
    if (!fileIds.length) { fail('Choisissez au moins un fichier.'); return; }
    const caption = ask.caption.trim();
    if (caption.length > 400) { fail('Légende trop longue (400 caractères au plus).'); return; }
    btn.disabled = true;
    try {
      const r = await logeStudio.propose({ projectId: data.project.id, folderId: f.id, step: f.step ?? data.project.currentStep, title, caption, fileIds, advancesStep: ask.advances, notify: ask.notify });
      showToast({ id: 0, ...mailToast(r, 'Demande publiée') });
      Object.assign(ask, { open: false, folderId: null, title: '', caption: '', advances: true, notify: true, touched: false });
      ask.selected = new Set();
      clearDone(ctx.projectId);
      ctx.refreshOverview();
      void load();
    } catch (e) {
      btn.disabled = false;
      fail(e instanceof Error ? e.message : 'Envoi impossible.');
    }
  }

  function addFiles(list: FileList | File[] | null): void {
    const f = folderOf(folderId);
    const c = client();
    if (!list || !data || !f || !c) return;
    const files = [...list].filter((x) => x.size > 0 || x.name);
    if (!files.length) return;
    if (f.kind === 'client') { showToast({ id: 0, kind: 'error', text: '« Vos envois » est le dossier du client : choisissez un dossier d’étape.' }); return; }
    // Une page ou une description `.360` de KeyShotXR, choisie comme simple fichier : on cherche l'export parmi les
    // fichiers choisis (vues comprises) ; seule, elle est refusée avec ce qu'il faut déposer à la place.
    if (files.some((x) => /\.(360|html?)$/i.test(x.name))) { void addItems(files.map((file) => ({ path: file.name, file }))); return; }
    enqueueUploads(files, {
      projectId: data.project.id, folderId: f.id, folderName: f.name, clientName: c.name, lang: c.lang,
      existing: (data.files ?? []).filter((x) => x.folderId === f.id).map((x) => ({ name: x.name, version: x.version })),
    });
  }

  /** Dépôt avec des dossiers (glissés, ou choisis) : export KeyShotXR reconnu, sinon fichier par fichier. */
  async function addItems(items: Array<{ path: string; file: File }>): Promise<void> {
    const f = folderOf(folderId);
    const c = client();
    if (!items.length || !data || !f || !c) return;
    if (f.kind === 'client') { showToast({ id: 0, kind: 'error', text: '« Vos envois » est le dossier du client : choisissez un dossier d’étape.' }); return; }
    try {
      const r = await enqueueDropped(items, {
        projectId: data.project.id, folderId: f.id, folderName: f.name, clientName: c.name, lang: c.lang,
        existing: (data.files ?? []).filter((x) => x.folderId === f.id).map((x) => ({ name: x.name, version: x.version })),
      }, (n) => confirm(`Ce dossier n’est pas un export KeyShotXR. Déposer ses ${n} fichiers un par un dans « ${f.name} » ?`));
      if (r.error) showToast({ id: 0, kind: 'error', text: r.error });
      else if (r.xr) showToast({ id: 0, kind: 'info', text: `Export KeyShotXR reconnu : « ${r.xr} » part en rendu 360°.${r.files ? ` ${plural(r.files, 'autre fichier', 'autres fichiers')} avec lui.` : ''}` });
    } catch (e) {
      showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Dossier illisible.' });
    }
  }

  async function addDropped(entries: FileSystemEntry[]): Promise<void> {
    try { await addItems(await walkEntries(entries)); }
    catch (e) { showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Dossier illisible.' }); }
  }

  function scrollToFile(id: string): void {
    requestAnimationFrame(() => {
      const row = root.querySelector<HTMLElement>(`[data-file-row="${CSS.escape(id)}"]`);
      if (row) { row.scrollIntoView({ block: 'center', behavior: 'smooth' }); setTimeout(() => { highlight = null; row.classList.remove('highlight'); }, 4000); }
    });
  }

  function openReport(id: string): void {
    // Lien signé fourni par le serveur : il s'ouvre tel quel, sans en-tête d'authentification.
    const signed = data?.reports.find((r) => r.id === id)?.url;
    if (signed) { window.open(signed, '_blank', 'noopener'); return; }
    // Sinon : fenêtre ouverte tout de suite (sinon bloquée), remplie avec la page lue avec le jeton du studio.
    const w = window.open('', '_blank');
    if (w) w.document.write('<!doctype html><meta charset="utf-8"><title>Compte rendu</title><p style="font:14px system-ui;padding:24px;color:#646880">Chargement du compte rendu…</p>');
    void logeStudio.reportHtml(id).then((html) => {
      if (!w || w.closed) { showToast({ id: 0, kind: 'error', text: 'Autorisez l’ouverture de fenêtres pour lire le compte rendu.' }); return; }
      w.document.open();
      w.document.write(html);
      w.document.close();
    }).catch((e: unknown) => {
      w?.close();
      showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Compte rendu illisible.' });
    });
  }

  on(root, 'click', '[data-go-overview]', () => ctx.go({ name: 'overview' }));
  on(root, 'click', '[data-go-client]', (_e, t) => ctx.go({ name: 'client', clientId: t.dataset.goClient! }));
  on(root, 'click', '[data-reload]', () => { error = null; void load(); });
  on(root, 'click', '[data-folder]', (_e, t) => selectFolder(t.dataset.folder!));
  on(root, 'click', '[data-step-folder]', (_e, t) => {
    const n = Number(t.dataset.stepFolder);
    const f = data?.folders.find((x) => x.kind === 'studio' && x.step === n && !x.parentId);
    if (f) selectFolder(f.id);
  });
  on(root, 'click', '[data-new-folder]', () => {
    if (!data) return;
    openNewFolder(data, folderId, (id) => { showToast({ id: 0, kind: 'info', text: 'Dossier créé.' }); if (id) folderId = id; void load(); });
  });
  on(root, 'change', '[data-file-input]', (_e, t) => {
    const input = t as HTMLInputElement;
    addFiles(input.files);
    input.value = '';
  });
  on(root, 'click', '[data-retry]', (_e, t) => retryUpload(t.dataset.retry!));
  on(root, 'click', '[data-dismiss]', (_e, t) => {
    const j = uploadJobs(ctx.projectId).find((x) => x.key === t.dataset.dismiss);
    if (j && (j.status === 'uploading' || j.status === 'preview') && !confirm(`Annuler l’envoi de « ${j.file.name} » ? Il n’apparaîtra pas dans la Loge.`)) return;
    dismissUpload(t.dataset.dismiss!);
  });
  on(root, 'click', '[data-clear-done]', () => clearDone(ctx.projectId));

  // Glisser-déposer : sur la zone, et sur toute la colonne des fichiers (cible plus large, même dossier).
  let dragDepth = 0;
  root.addEventListener('dragenter', (e) => {
    if (!e.dataTransfer?.types.includes('Files') || !(e.target as HTMLElement).closest('[data-files-col]')) return;
    e.preventDefault();
    dragDepth++;
    root.querySelector('[data-drop]')?.classList.add('over');
  });
  root.addEventListener('dragover', (e) => {
    if (!e.dataTransfer?.types.includes('Files') || !(e.target as HTMLElement).closest('[data-files-col]')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = root.querySelector('[data-drop]') ? 'copy' : 'none';
  });
  root.addEventListener('dragleave', (e) => {
    if (!(e.target as HTMLElement).closest('[data-files-col]')) return;
    dragDepth = Math.max(0, dragDepth - 1);
    if (!dragDepth) root.querySelector('[data-drop]')?.classList.remove('over');
  });
  root.addEventListener('drop', (e) => {
    if (!(e.target as HTMLElement).closest('[data-files-col]')) return;
    e.preventDefault();
    dragDepth = 0;
    root.querySelector('[data-drop]')?.classList.remove('over');
    if (!root.querySelector('[data-drop]')) return;
    // Un dossier (export KeyShotXR, ou dossier de rendus) se lit par ses entrées, tout de suite.
    const entries = droppedEntries(e.dataTransfer);
    if (entries) void addDropped(entries);
    else addFiles(e.dataTransfer?.files ?? null);
  });
  on(root, 'click', '[data-pick-folder]', () => root.querySelector<HTMLInputElement>('[data-folder-input]')?.click());
  on(root, 'change', '[data-folder-input]', (_e, t) => {
    const input = t as HTMLInputElement;
    const items = [...(input.files ?? [])].map((file) => ({ path: file.webkitRelativePath || file.name, file }));
    input.value = '';
    void addItems(items);
  });

  // Légende : enregistrée en quittant le champ (ou Entrée).
  on(root, 'keydown', '[data-caption]', (e, t) => { if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); (t as HTMLInputElement).blur(); } });
  on(root, 'change', '[data-caption]', (_e, t) => {
    const input = t as HTMLInputElement;
    const f = data?.files.find((x) => x.id === input.dataset.caption);
    if (!f) return;
    const caption = input.value.replace(/\s+/g, ' ').trim();
    if (caption === f.caption) return;
    input.disabled = true;
    void logeStudio.updateFile({ fileId: f.id, caption })
      .then(() => { f.caption = caption; showToast({ id: 0, kind: 'info', text: caption ? 'Légende enregistrée.' : 'Légende retirée.' }); })
      .catch((e: unknown) => { input.value = f.caption; showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Légende non enregistrée.' }); })
      .finally(() => { input.disabled = false; });
  });
  on(root, 'change', '[data-allow]', (_e, t) => {
    const input = t as HTMLInputElement;
    const f = data?.files.find((x) => x.id === input.dataset.allow);
    if (!f) return;
    const allow = input.checked;
    if (allow && !confirm(`Autoriser dès maintenant le téléchargement de l’original « ${f.name} » ? Le client pourra le récupérer avant le règlement du projet.`)) { input.checked = false; return; }
    input.disabled = true;
    void logeStudio.updateFile({ fileId: f.id, downloadable: allow })
      .then(() => { f.allowDownload = allow; showToast({ id: 0, kind: 'info', text: allow ? 'Téléchargement autorisé pour ce fichier.' : 'Retour en consultation seule.' }); })
      .catch((e: unknown) => { input.checked = !allow; showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Réglage non enregistré.' }); })
      .finally(() => { input.disabled = false; });
  });
  // Maquette 3D : la visionneuse de La Loge, chargée à la demande (three.js n'alourdit pas World).
  on(root, 'click', '[data-view3d]', (_e, t) => {
    const f = data?.files.find((x) => x.id === t.dataset.view3d);
    if (!f?.model) return;
    void import('../loge/viewer3d')
      .then((m) => m.openModel3d({
        url: f.model!, title: f.caption || f.name, eyebrow: `Maquette 3D · V${f.version}`, lang: 'fr',
        onClose: () => root.querySelector<HTMLElement>(`[data-view3d="${CSS.escape(f.id)}"]`)?.focus(),
      }))
      .catch(() => showToast({ id: 0, kind: 'error', text: 'La visionneuse 3D n’a pas pu se charger.' }));
  });
  // Rendu 360° : le lecteur de La Loge, chargé à la demande, sur les vues signées pour le studio.
  on(root, 'click', '[data-viewxr]', (_e, t) => {
    const f = data?.files.find((x) => x.id === t.dataset.viewxr);
    if (!f?.xr) return;
    const xr = f.xr;
    void import('../loge/viewerXr')
      .then((m) => m.openXr({
        frames: f.previews, xr, title: f.caption || f.name, eyebrow: `Rendu 360° · V${f.version}`, lang: 'fr',
        onClose: () => root.querySelector<HTMLElement>(`[data-viewxr="${CSS.escape(f.id)}"]`)?.focus(),
      }))
      .catch(() => showToast({ id: 0, kind: 'error', text: 'Le lecteur 360° n’a pas pu se charger.' }));
  });
  // Vidéo : la visionneuse de La Loge, dans l'état où le client la verra (lecture seule, filigrane à l'écran).
  on(root, 'click', '[data-play]', (_e, t) => {
    const f = data?.files.find((x) => x.id === t.dataset.play);
    if (!f?.stream) return;
    void import('../loge/viewer')
      .then((m) => m.openViewer([{ pages: [], title: f.caption || f.name, video: f.stream }], 0, { lang: 'fr', clientName: data?.client?.name ?? '' }))
      .catch(() => showToast({ id: 0, kind: 'error', text: 'La visionneuse n’a pas pu se charger.' }));
  });
  on(root, 'click', '[data-archive-project]', (_e, t) => {
    if (!data) return;
    const archive = t.dataset.archiveProject === '1';
    (t as HTMLButtonElement).disabled = true;
    void logeStudio.setProjectStatus(ctx.projectId, archive ? 'archived' : 'active')
      .then(() => {
        showToast({ id: 0, kind: 'info', text: archive ? `« ${data!.project.name} » est archivé : le client ne le voit plus.` : `« ${data!.project.name} » est rouvert.` });
        ctx.refreshOverview();
        return load().then(paint);
      })
      .catch((e: unknown) => showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'État non enregistré.' }))
      .finally(() => { (t as HTMLButtonElement).disabled = false; });
  });
  on(root, 'click', '[data-delete-project]', () => {
    if (!data) return;
    const p = data.project;
    // Ce qui part : les fichiers visibles **et** ceux de la corbeille, qui s'effacent aussi. Une confirmation ne
    // doit jamais annoncer moins que ce qu'elle détruit.
    const corbeille = data.files.filter((f) => f.deletedAt && !f.incomplete).length;
    const fichiers = data.files.filter((f) => !f.incomplete).length;
    const compte = corbeille ? `${plural(fichiers, 'fichier', 'fichiers')} (dont ${corbeille} à la corbeille)` : plural(fichiers, 'fichier', 'fichiers');
    // On n'énumère que ce qui existe : « 1 fichier » se lit, « 1 fichier, 0 demande, 0 compte rendu » non.
    const contents = [fichiers ? compte : '', data.proposals.length ? plural(data.proposals.length, 'demande', 'demandes') : '', data.reports.length ? plural(data.reports.length, 'compte rendu', 'comptes rendus') : '']
      .filter(Boolean).join(', ') || 'ses dossiers et ses étapes';
    openDeleteDialog({
      kind: 'projet',
      name: p.name,
      contents,
      onDelete: async (confirm) => {
        const r = await logeStudio.deleteProject(p.id, confirm);
        showToast({
          id: 0, kind: r.storageError ? 'error' : 'info',
          text: r.storageError
            ? `« ${p.name} » est supprimé, mais ses fichiers n’ont pas tous pu être effacés du stockage.`
            : `« ${p.name} » est supprimé, avec ${plural(r.files, 'fichier', 'fichiers')}.`,
        });
        ctx.refreshOverview();
        ctx.go(data?.client ? { name: 'client', clientId: data.client.id } : { name: 'overview' });
      },
    });
  });
  on(root, 'click', '[data-trash]', (_e, t) => {
    const f = data?.files.find((x) => x.id === t.dataset.trash);
    if (!f) return;
    const inDecision = data!.proposals.some((x) => isWaiting(x.status) && x.fileIds.includes(f.id));
    if (inDecision && !confirm(`« ${f.name} » fait partie d’une décision en attente. Le mettre quand même à la corbeille ?\n\nS’il n’y reste plus aucun fichier, la demande est retirée : le client ne la voit plus.`)) return;
    void logeStudio.updateFile({ fileId: f.id, deleted: true })
      .then((r) => {
        const gone = r.withdrawn.map((x) => `« ${x.title} »`).join(', ');
        showToast({
          id: 0, kind: 'info',
          text: gone ? `« ${f.name} » est à la corbeille. Demande retirée chez le client : ${gone}.` : `« ${f.name} » est à la corbeille (30 jours).`,
          ...(gone ? {} : { undo: async () => { await logeStudio.updateFile({ fileId: f.id, deleted: false }); void load(); } }),
        });
        if (gone) ctx.refreshOverview();
        void load();
      })
      .catch((e: unknown) => showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Impossible de retirer le fichier.' }));
  });
  on(root, 'click', '[data-restore]', (_e, t) => {
    void logeStudio.updateFile({ fileId: t.dataset.restore!, deleted: false })
      .then(() => { showToast({ id: 0, kind: 'info', text: 'Fichier restauré.' }); void load(); })
      .catch((e: unknown) => showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Restauration impossible.' }));
  });
  on(root, 'click', '[data-download]', (_e, t) => {
    const btn = t as HTMLButtonElement;
    btn.disabled = true;
    void logeStudio.download(t.dataset.download!)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = Object.assign(document.createElement('a'), { href: url, download: t.dataset.name ?? 'fichier' });
        document.body.append(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      })
      .catch((e: unknown) => showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Téléchargement impossible.' }))
      .finally(() => { btn.disabled = false; });
  });
  on(root, 'click', '[data-unlock-project]', (_e, t) => {
    if (!data) return;
    const open = t.dataset.lock !== 'true';
    if (!confirm(open
      ? `Débloquer les téléchargements de « ${data.project.name} » ?\n\nLes personnes invitées pourront télécharger les originaux de ce projet. À faire une fois le projet entièrement réglé.`
      : `Refermer les téléchargements de « ${data.project.name} » ?\n\nRetour à la consultation seule, sauf les fichiers marqués « Téléchargeable ».`)) return;
    const btn = t as HTMLButtonElement;
    btn.disabled = true;
    void logeStudio.unlock({ projectId: data.project.id }, open)
      .then(() => { showToast({ id: 0, kind: 'info', text: open ? 'Téléchargements débloqués pour ce projet.' : 'Téléchargements refermés pour ce projet.' }); ctx.refreshOverview(); void load(); })
      .catch((e: unknown) => { btn.disabled = false; showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Réglage impossible.' }); });
  });
  on(root, 'click', '[data-report]', (_e, t) => openReport(t.dataset.report!));
  on(root, 'click', '[data-withdraw]', (_e, t) => {
    const x = data?.proposals.find((p) => p.id === t.dataset.withdraw);
    if (!x || !confirm(`Retirer la demande « ${x.title} » ?\n\nElle disparaît de la Loge du client (panneau, « À traiter »). Les fichiers restent dans leur dossier.`)) return;
    const btn = t as HTMLButtonElement;
    btn.disabled = true;
    void logeStudio.withdraw(x.id)
      .then(() => { showToast({ id: 0, kind: 'info', text: `Demande « ${x.title} » retirée.` }); ctx.refreshOverview(); void load(); })
      .catch((e: unknown) => { btn.disabled = false; showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Retrait impossible.' }); });
  });
  on(root, 'click', '[data-make-current]', (_e, t) => {
    if (!data) return;
    const n = Number(t.dataset.makeCurrent);
    const s = data.project.steps[n - 1];
    if (!s) return;
    const back = n < data.project.currentStep;
    if (!confirm(back
      ? `Rouvrir « ${pad2(n)} · ${stepLabel(s, 'fr')} » ?\n\nElle redevient l’étape en cours : vous pourrez y demander une nouvelle décision. La validation déjà faite et son compte rendu restent dans l’historique.`
      : `Passer le projet à « ${pad2(n)} · ${stepLabel(s, 'fr')} » sans validation du client ?`)) return;
    const btn = t as HTMLButtonElement;
    btn.disabled = true;
    void logeStudio.setStep(data.project.id, n)
      .then(() => { showToast({ id: 0, kind: 'info', text: `Étape en cours : ${pad2(n)} · ${stepLabel(s, 'fr')}.` }); ctx.refreshOverview(); void load(); })
      .catch((e: unknown) => { btn.disabled = false; showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Changement d’étape impossible.' }); });
  });
  on(root, 'change', '[data-cover-input]', (_e, t) => {
    const input = t as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const c = client();
    if (!file || !data || !c) return;
    // Le cadrage se décide ici, pas au hasard du rognage par le navigateur chez le client.
    openCoverCrop(file, (cropped) => {
      if (!data || !c) return;
      enqueueUploads([cropped], { projectId: data.project.id, folderId: '', folderName: 'Couverture', clientName: c.name, lang: c.lang, existing: [], cover: true });
      paint();
    });
  });
  on(root, 'submit', '[data-reply]', (e, t) => {
    e.preventDefault();
    const form = t as HTMLFormElement;
    const input = el<HTMLInputElement>(form, 'input');
    const body = input.value.replace(/\s+/g, ' ').trim();
    if (!body) { input.focus(); return; }
    const btn = el<HTMLButtonElement>(form, 'button');
    btn.disabled = true;
    void logeStudio.reply(form.dataset.reply!, body)
      .then((r) => { showToast({ id: 0, ...mailToast(r, 'Réponse envoyée') }); input.value = ''; ctx.refreshOverview(); void load(); })
      .catch((err: unknown) => { btn.disabled = false; showToast({ id: 0, kind: 'error', text: err instanceof Error ? err.message : 'Réponse impossible.' }); });
  });

  // Panneau « Demander une décision ».
  on(root, 'click', '[data-ask-open]', () => openAsk());
  on(root, 'click', '[data-ask-close]', () => { ask.open = false; paint(); });
  on(root, 'change', '[data-ask-file]', (_e, t) => { const i = t as HTMLInputElement; if (i.checked) ask.selected.add(i.dataset.askFile!); else ask.selected.delete(i.dataset.askFile!); });
  on(root, 'input', '[data-ask-title]', (_e, t) => { ask.title = (t as HTMLInputElement).value; ask.touched = true; });
  on(root, 'input', '[data-ask-caption]', (_e, t) => { ask.caption = (t as HTMLTextAreaElement).value; });
  on(root, 'change', '[data-ask-advances]', (_e, t) => { ask.advances = (t as HTMLInputElement).checked; });
  on(root, 'change', '[data-ask-notify]', (_e, t) => { ask.notify = (t as HTMLInputElement).checked; });
  on(root, 'click', '[data-ask-send]', (_e, t) => void sendAsk(t as HTMLButtonElement));

  // Une relecture repoussée pendant une saisie se fait en quittant le champ.
  root.addEventListener('focusout', () => { if (pendingReload) setTimeout(() => { if (pendingReload && !destroyed) reload(); }, 0); });
  // Aperçu illisible (lien expiré…) : on remet l'étiquette du format au lieu d'une image cassée.
  root.addEventListener('error', (e) => {
    const img = e.target;
    if (img instanceof HTMLImageElement && img.matches('[data-thumb]')) {
      const box = img.parentElement;
      if (box) { box.classList.add('tag'); box.textContent = img.dataset.tag ?? ''; }
    }
  }, true);

  void load();

  return {
    projectId: ctx.projectId,
    destroy: () => { destroyed = true; offUploads(); if (reloadTimer) clearTimeout(reloadTimer); },
    focus: (fid, file) => {
      if (file) { highlight = file; const f = data?.files.find((x) => x.id === file); if (f) folderId = f.folderId; paint(); scrollToFile(file); return; }
      if (fid && folderOf(fid)) selectFolder(fid);
    },
    setOverview: (o) => { overview = o; if (data && !data.client) paint(); },
    refresh: () => reload(),
  };
}
